import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Microphone } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import MaterialCatalogAutocomplete from "@/components/MaterialCatalogAutocomplete";

/**
 * Press-and-hold mic that records the tech's closing brief for an intervention,
 * uploads to /api/interventions/{id}/voice-close (Whisper + Claude), then opens
 * a preview dialog where the tech validates the AI-parsed fields before they
 * hit the database. Fields applied on validation:
 *   - `notes` (client + internal notes concatenated), `status=done`
 *   - Material order auto-created with the parsed items
 */
export default function VoiceCloseButton({ intervention, onApplied, size = "sm" }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null); // { transcription, structured }
  const [saving, setSaving] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedRef = useRef(false);

  const startRec = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        setRecording(false);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        startedRef.current = false;
        if (blob.size < 500) { toast.error("Enregistrement trop court"); setElapsed(0); return; }
        await upload(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      startedRef.current = false;
      toast.error("Micro refusé — autorise l'accès dans les paramètres");
    }
  };

  const stopRec = () => {
    if (!startedRef.current) return;
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "close.webm");
      fd.append("language", "fr");
      const r = await api.post(`/interventions/${intervention.id}/voice-close`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); setElapsed(0); }
  };

  const apply = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const s = preview.structured || {};
      const noteParts = [];
      if ((s.client_summary || "").trim()) noteParts.push(s.client_summary.trim());
      if ((s.notes_internal || "").trim()) noteParts.push(`\n[Notes internes]\n${s.notes_internal.trim()}`);
      const notes = noteParts.join("\n\n") || preview.transcription;
      const patch = { notes };
      if (s.mark_done) patch.status = "done";
      await api.patch(`/interventions/${intervention.id}`, patch);
      if ((s.materials || []).length > 0 && intervention.chantier_id) {
        try {
          await api.post(`/material-orders`, {
            chantier_id: intervention.chantier_id,
            items: s.materials.map((m) => ({ label: m.label, quantity: m.quantity || 1, unit_price: m.unit_price || 0 })),
            note: `Auto — clôture vocale du ${new Date().toLocaleDateString("fr-FR")}`,
          });
        } catch (err) { console.warn("material-order failed:", err); }
      }
      toast.success(s.mark_done ? "Intervention clôturée · rapport IA appliqué" : "Rapport IA appliqué");
      setPreview(null);
      onApplied?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // Editable-in-place fields on the preview dialog
  const updateStructured = (patch) => setPreview((p) => ({ ...p, structured: { ...(p?.structured || {}), ...patch } }));
  const updateMaterial = (i, patch) => setPreview((p) => {
    const mats = [...(p?.structured?.materials || [])];
    mats[i] = { ...mats[i], ...patch };
    return { ...p, structured: { ...p.structured, materials: mats } };
  });
  const removeMaterial = (i) => setPreview((p) => {
    const mats = [...(p?.structured?.materials || [])];
    mats.splice(i, 1);
    return { ...p, structured: { ...p.structured, materials: mats } };
  });
  const addMaterial = () => setPreview((p) => ({
    ...p,
    structured: { ...p.structured, materials: [...(p.structured.materials || []), { label: "", quantity: 1, unit_price: 0 }] },
  }));

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const dim = size === "xs" ? 22 : size === "sm" ? 28 : 34;

  return (
    <>
      <button
        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startRec(); }}
        onPointerUp={(e) => { e.stopPropagation(); e.preventDefault(); stopRec(); }}
        onPointerCancel={(e) => { e.stopPropagation(); e.preventDefault(); stopRec(); }}
        onPointerLeave={(e) => { if (recording) { e.stopPropagation(); e.preventDefault(); stopRec(); } }}
        onClick={(e) => e.stopPropagation()}
        disabled={uploading || saving}
        className={`shrink-0 rounded-full flex items-center justify-center transition-all touch-none select-none ${recording ? "bg-red-500 text-white scale-110 shadow-lg shadow-red-500/40 animate-pulse" : "bg-[#005CFF]/20 text-[#7CB2FF] border border-[#005CFF]/40 hover:bg-[#005CFF] hover:text-white active:scale-95"} disabled:opacity-40`}
        style={{ width: dim, height: dim }}
        title={recording ? "Relâche pour envoyer" : "Maintiens pour dicter la clôture (IA)"}
        data-testid={`voice-close-btn-${intervention.id}`}
      >
        {uploading ? (
          <div className="w-3 h-3 border border-white/60 border-t-white rounded-full animate-spin" />
        ) : recording ? (
          <span className="text-[9px] font-mono tabular-nums font-bold">{mm}:{ss}</span>
        ) : (
          <Microphone size={dim * 0.5} weight="fill" />
        )}
      </button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && !saving && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="voice-close-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Microphone size={18} weight="fill" className="text-[#005CFF]" />
              Clôture IA · {intervention.title}
            </DialogTitle>
          </DialogHeader>

          {preview && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Ce que tu as dit</div>
                <div className="text-xs whitespace-pre-wrap italic text-white/80" data-testid="voice-close-transcript">« {preview.transcription} »</div>
                {typeof preview.structured?.confidence === "number" && (
                  <div className="mt-1.5 text-[9px] text-white/40">
                    Confiance IA : {(preview.structured.confidence * 100).toFixed(0)}%
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Résumé pour le rapport CLIENT</div>
                <Textarea
                  rows={3}
                  value={preview.structured?.client_summary || ""}
                  onChange={(e) => updateStructured({ client_summary: e.target.value })}
                  data-testid="voice-close-client-summary"
                />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Notes internes équipe</div>
                <Textarea
                  rows={3}
                  value={preview.structured?.notes_internal || ""}
                  onChange={(e) => updateStructured({ notes_internal: e.target.value })}
                  data-testid="voice-close-notes-internal"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Matériel utilisé</div>
                  <button onClick={addMaterial} className="text-[10px] text-[#7CB2FF] hover:text-white" data-testid="voice-close-add-material">+ Ajouter une ligne</button>
                </div>
                {(preview.structured?.materials || []).length === 0 && (
                  <div className="text-[11px] italic text-white/40 px-1 py-2">Aucun matériel détecté.</div>
                )}
                <div className="space-y-1.5">
                  {(preview.structured?.materials || []).map((m, i) => (
                    <div key={`mat-${i}-${m.label || "new"}`} className="grid grid-cols-[1fr_60px_80px_28px] gap-1.5 items-center">
                      <MaterialCatalogAutocomplete
                        value={m.label || ""}
                        onChange={(v) => updateMaterial(i, { label: v })}
                        onSelect={(item) => updateMaterial(i, {
                          label: item.name,
                          unit_price: item.unit_price != null && (!m.unit_price || m.unit_price === 0) ? item.unit_price : m.unit_price,
                        })}
                        placeholder="Article — commence à taper…"
                        testid={`voice-close-material-label-${i}`}
                      />
                      <Input type="number" min="1" value={m.quantity} onChange={(e) => updateMaterial(i, { quantity: parseInt(e.target.value, 10) || 1 })} data-testid={`voice-close-material-qty-${i}`} />
                      <Input type="number" step="0.01" min="0" value={m.unit_price} onChange={(e) => updateMaterial(i, { unit_price: parseFloat(e.target.value) || 0 })} data-testid={`voice-close-material-price-${i}`} />
                      <button onClick={() => removeMaterial(i)} className="text-white/40 hover:text-red-400 text-lg" data-testid={`voice-close-material-remove-${i}`}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!preview.structured?.mark_done}
                  onChange={(e) => updateStructured({ mark_done: e.target.checked })}
                  className="accent-[#005CFF]"
                  data-testid="voice-close-mark-done"
                />
                Passer l&apos;intervention en <b>terminée</b>
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPreview(null)} disabled={saving} data-testid="voice-close-cancel">Annuler</Button>
            <Button onClick={apply} disabled={saving} className="bg-[#005CFF] hover:bg-[#0047CC]" data-testid="voice-close-apply">
              {saving ? "Application…" : "Appliquer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
